<!--
// app/components/pages/teams-team/CardCoachTeamPage.vue
-->
<script setup lang="ts">
import type { StaffRoles } from "~/src/Shared/Types";
import { useImages } from "~/app/composables/images";
import { setAliasUrl } from "~/src/Shared/utils";

type Props = {
  teamFirstColorHEX: string | undefined | null;
  teamSecondColorHEX: string | undefined | null;
  staffCompleteName: string | undefined | null;
  staffRole: StaffRoles;
  staffImage?: string;
};

const props = defineProps<Props>();

const { playerPlaceholderPath } = useImages();

const defaultColor = "#888888";
</script>

<template>
  <div class="staff-card-container">
    <div class="h-full relative z-[3]">
      <div
        class="clip-diagonal-right"
        :style="`background-image: linear-gradient(180deg, ${
          teamFirstColorHEX ? teamFirstColorHEX : defaultColor
        }99 40%, ${teamSecondColorHEX ? teamSecondColorHEX : defaultColor}99 100%);`"
      ></div>
      <div
        class="clip-diagonal-left"
        :style="`background-image: linear-gradient(180deg, ${
          teamFirstColorHEX ? teamFirstColorHEX : defaultColor
        }30 0%, ${teamSecondColorHEX ? teamSecondColorHEX : defaultColor}30 100%);`"
      ></div>
    </div>

    <div class="staff-card-content">
      <div class="container-staff-img">
        <NuxtImg
          :src="staffImage ? setAliasUrl(staffImage) : playerPlaceholderPath"
          :alt="''"
          width="456"
          height="642"
          sizes="128px sm:122px"
        />
      </div>

      <div class="w-full min-h-full flex flex-col lg:justify-between">
        <div class="container-staff-info">
          <div class="container-staff-info-text">
            <div>
              <p v-if="staffCompleteName" class="staff-name">{{ staffCompleteName }}</p>
              <p class="staff-role">{{ staffRole ? $t(`${staffRole}`) : $t("coach") }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.staff-card-container {
  @apply md:rounded-lg bg-gray-900 relative h-[calc(180px+2rem)] bigsm:h-[285px] md:h-[204px] overflow-hidden;
}

.clip-diagonal-right {
  @apply h-full w-[155px] md:w-[135px] absolute;
  clip-path: polygon(0 0, 100% 0%, 22% 100%, 0% 100%);
}

.clip-diagonal-left {
  @apply w-[65px] h-[158px] md:translate-x-2 bg-opacity-20 absolute bottom-0 right-0;
  clip-path: polygon(100% 0, 100% 0%, 100% 100%, 0% 100%);
}

@media (max-width: 1025px) {
  .clip-diagonal-right,
  .player-card-container:hover div.clip-diagonal-right {
    clip-path: polygon(0 0, 100% 0%, 0% 100%, 0% 100%) !important;
  }
}

.staff-card-content {
  @apply grid grid-rows-[180px_1fr] grid-cols-[136px_1fr] gap-y-2 md:gap-y-0 md:flex md:justify-start items-start h-full w-full absolute top-0 right-0 bottom-0 left-0 z-[5] p-4 md:p-3;
}

.container-staff-info {
  @apply flex justify-start items-start w-full;
}

.container-staff-img {
  @apply shrink-0 relative rounded bg-[url('/img/bg-card/bg-card-player-image.jpg')] bg-cover bg-center bg-no-repeat max-w-[136px] h-[180px] md:h-full overflow-hidden mr-3.5;
}

.container-staff-img::after {
  content: "";
  background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 51.94%);
  @apply absolute bottom-0 w-full h-20 z-[2];
}

.container-staff-img img {
  @apply w-full h-full object-top object-cover;
}

.container-staff-info-text {
  @apply flex justify-between items-start min-w-full;
}

.staff-name {
  @apply font-bold;
}

.staff-role {
  @apply text-gray-400 text-sm leading-[21px] capitalize;
}

.clip-diagonal-right,
.container-staff-info-text {
  @apply transition-all ease-in-out duration-200;
}

.staff-card-container:hover div.clip-diagonal-right {
  @apply md:w-[147px];
  clip-path: polygon(0 0, 100% 0%, 29% 100%, 0% 100%);
}

.staff-card-container:hover .container-staff-info-text {
  @apply pl-2;
}
</style>
